import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchProducts } from '../lib/cms';
import KatalogSection from '../components/KatalogSection';

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const heroImages = [
    "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/heined-0117.jpg",
    "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/l-cubisme.jpg",
    "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/l-kvadrat.jpg",
    "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/twist-70x140-med-skif-16-12-15.jpg"
  ];

  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Already loaded in the other useEffect
  }, []);

  // Filter categories based on terms
  const categories = [
    { slug: 'all', label: 'Alle' },
    { slug: 'tables', label: 'Borde' },
    { slug: 'sofas-seatings', label: 'Sofaer & Stole' },
    { slug: 'other-furnitures', label: 'Andre møbler' },
    { slug: 'uncategorized', label: 'Uklassificeret' }
  ];

  const filteredProducts = activeFilter === 'all'
    ? products.slice(0, 8) // Show top 8 on home page
    : products.filter(p => p.terms?.['product-category']?.includes(activeFilter)).slice(0, 8);

  // Mock Instagram data for Carpentry (Tømrerarbejde)
  const [carpentryFeed, setCarpentryFeed] = useState([
    {
      id: 1,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/heine-280808-03.jpg",
      likes: 124,
      comments: 8,
      link: "https://www.instagram.com/toemmertonyheine/"
    },
    {
      id: 2,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/heini-20140221-03d1.jpg",
      likes: 98,
      comments: 4,
      link: "https://www.instagram.com/toemmertonyheine/"
    },
    {
      id: 3,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/img-2147-scaled.jpg",
      likes: 145,
      comments: 12,
      link: "https://www.instagram.com/toemmertonyheine/"
    },
    {
      id: 4,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/heine-2-bench-hd1.jpg",
      likes: 112,
      comments: 6,
      link: "https://www.instagram.com/toemmertonyheine/"
    }
  ]);

  // Mock Instagram data for Furniture (Møbler)
  const [furnitureFeed, setFurnitureFeed] = useState([
    {
      id: 1,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/heined-0117.jpg",
      likes: 342,
      comments: 18,
      link: "https://www.instagram.com/heinedesign/"
    },
    {
      id: 2,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/l-cubisme.jpg",
      likes: 289,
      comments: 14,
      link: "https://www.instagram.com/heinedesign/"
    },
    {
      id: 3,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/l-kvadrat.jpg",
      likes: 412,
      comments: 25,
      link: "https://www.instagram.com/heinedesign/"
    },
    {
      id: 4,
      image: "https://assets.auxodev.dk/assets/f345d65a-1d50-4485-b2fa-cf1f9f3e4dca/twist-70x140-med-skif-16-12-15.jpg",
      likes: 256,
      comments: 9,
      link: "https://www.instagram.com/heinedesign/"
    }
  ]);

  useEffect(() => {
    async function loadData() {
      const data = await fetchProducts();
      // Filter active products and sort them
      const activeProducts = data.filter(p => p.active !== false);
      setProducts(activeProducts);
      setLoading(false);
    }
    loadData();

    async function fetchFeeds() {
      try {
        const resFurniture = await fetch('https://api.dev.auxo.dk/fn/instagram-feed?username=heinedesign');
        if (resFurniture.ok) {
          const data = await resFurniture.json();
          if (Array.isArray(data) && data.length > 0) {
            setFurnitureFeed(data.slice(0, 4));
          }
        }
      } catch (err) {
        console.error('Error fetching furniture feed:', err);
      }

      try {
        const resCarpentry = await fetch('https://api.dev.auxo.dk/fn/instagram-feed?username=toemmertonyheine');
        if (resCarpentry.ok) {
          const data = await resCarpentry.json();
          if (Array.isArray(data) && data.length > 0) {
            setCarpentryFeed(data.slice(0, 4));
          }
        }
      } catch (err) {
        console.error('Error fetching carpentry feed:', err);
      }
    }
    fetchFeeds();
  }, []);

  return (
    <div className="animate-fade-in">
      <Helmet>
        <title>Heine Design – Dansk håndværk og design</title>
        <meta name="description" content="Eksklusive håndlavede møbler og specialdesignede løsninger i dansk kvalitet. Heine Design kombinerer traditionelt håndværk med moderne design og skræddersyede løsninger." />
        <meta property="og:title" content="Heine Design" />
        <meta property="og:description" content="Eksklusive håndlavede møbler og specialdesignede løsninger i dansk kvalitet." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Background Images with Cross-fade */}
        <div className="absolute inset-0 z-0">
          {heroImages.map((img, index) => (
            <img
              key={img}
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
              alt="Heine Design Eksklusive Møbler"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[4000ms] ease-in-out ${
                index === currentHeroIndex ? 'opacity-50' : 'opacity-0'
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex justify-center">
          <div className="max-w-xl text-center space-y-6 text-white animate-slide-up flex flex-col items-center">
            <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-brand-oak font-medium font-display block">
              Dansk håndværk og design
            </span>
            <h1 className="text-6xl md:text-8xl font-medium tracking-wider uppercase leading-none font-display">
              Heine Design
            </h1>

            <p className="text-sm md:text-base text-brand-charcoal leading-relaxed font-light tracking-wide max-w-xl">
              Eksklusive, minimalistiske og håndlavede møbler i rustfrit stål,<br />egetræ og italiensk skifer. Skabt til at holde i generationer.
            </p>

            <div className="flex flex-row gap-4 pt-4 justify-center">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 bg-brand-oak text-black text-xs font-medium tracking-[0.2em] uppercase hover:bg-brand-oak-dark transition-colors duration-300 rounded-none text-center whitespace-nowrap"
              >
                Kontakt
              </Link>
              <Link
                to="/produkter"
                className="px-6 py-3.5 border border-white/20 text-white text-xs font-medium tracking-[0.2em] uppercase hover:bg-white hover:text-black hover:border-white transition-all duration-300 rounded-none text-center whitespace-nowrap"
              >
                Se produkter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Om Heine Design (Bilingual Section) */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Dansk */}
            <div className="space-y-6 border-r border-brand-gray pr-0 md:pr-16">
              <div className="space-y-2">
                <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                  Om os
                </span>
              </div>
              <div className="space-y-4 text-sm text-brand-charcoal leading-relaxed font-light">
                <h4 className="font-display text-xl font-light text-white tracking-[0.15em] uppercase leading-snug mb-6">Minimalistisk Skandinavisk Design</h4>
                <p>Vi er en dansk møbeldesignvirksomhed, der specialiserer sig i moderne design.</p>
                <p>Vores virksomhed har eksisteret siden 1998. I den tid har vi udviklet nye og innovative møbler, primært i rustfrit stål og af høj kvalitet.</p>
                <p>Alle produkter er håndlavede og udviklet i Danmark.</p>
                <p>Nu har vi etableret et stærkt fundament for vores virksomhed i Skandinavien, og vi søger nu nye kontakter i udlandet.</p>
              </div>
            </div>

            {/* English */}
            <div className="space-y-6">
              <div className="space-y-2">
                <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                  About us
                </span>
              </div>
              <div className="space-y-4 text-sm text-brand-charcoal leading-relaxed font-light">
                <h4 className="font-display text-xl font-light text-white tracking-[0.15em] uppercase leading-snug mb-6">Minimalistic Scandinavian Design</h4>
                <p>We are a Danish furniture design company who specializes in modern design.</p>
                <p>Our company has existed since 1998. In that time we have developed new and innovative furniture mostly in stainless steel and high quality.</p>
                <p>All products are handmade and developed in Denmark.</p>
                <p>Now we have established a strong foundation for our company in Scandinavia and now we seek new contacts abroad.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Udvalgte Produkter */}
      <section className="py-24 bg-brand-light/30 border-t border-brand-gray">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="space-y-4">
              <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
                Vores kollektion
              </span>
              <h2 className="text-3xl font-light tracking-tight text-white uppercase">
                Udvalgte Produkter
              </h2>
              <div className="h-[1px] w-20 bg-brand-oak" />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveFilter(cat.slug)}
                  className={`px-4 py-2 text-xs tracking-wider uppercase transition-all duration-300 rounded-none border ${
                    activeFilter === cat.slug
                      ? 'bg-brand-oak text-black border-brand-oak font-semibold'
                      : 'bg-brand-light text-brand-charcoal border-brand-gray hover:border-brand-oak hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <i className="fa-solid fa-spinner animate-spin text-2xl text-brand-oak" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {filteredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/produkter/${product.slug}`}
                  className="group flex flex-col bg-brand-light border border-brand-gray rounded-sm overflow-hidden transition-all duration-300 hover:shadow-black/40 hover:shadow-xl hover:-translate-y-1"
                >
                  <div className="aspect-square overflow-hidden bg-black relative rounded-sm">
                    <img
                      src={product.product_images?.[0] || product.gallery?.[0]}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 rounded-sm"
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-xs tracking-wider uppercase font-medium text-white group-hover:text-brand-oak transition-colors duration-300 line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-[11px] text-brand-charcoal/60 tracking-widest uppercase">
                        {product.terms?.['product-type']?.[0]?.replace('-', ' ')}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-white">
                      {product.price}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center pt-12">
            <Link
              to="/produkter"
              className="px-8 py-4 border border-white text-white text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all duration-300 rounded-none inline-block"
            >
              Se alle produkter
            </Link>
          </div>
        </div>
      </section>

      {/* Katalog Section */}
      <KatalogSection />

      {/* Instagram Integration */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
              Følg mig på sociale medier
            </span>
            <h2 className="text-3xl font-light tracking-tight text-white uppercase">
              Instagram Feeds
            </h2>
            <div className="h-[1px] w-20 bg-brand-oak mx-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Feed 1: Møbler */}
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-brand-gray pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center border border-brand-gray">
                    <i className="fa-solid fa-couch text-brand-oak" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold tracking-wider text-white">@heinedesign</h3>
                    <p className="text-[10px] text-brand-charcoal tracking-widest uppercase">Møbler & Design</p>
                  </div>
                </div>
                <a
                  href="https://www.instagram.com/heinedesign/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs tracking-wider text-white hover:text-brand-oak uppercase font-medium"
                >
                  Følg
                </a>
              </div>
            </div>

            {/* Feed 2: Tømrerarbejde */}
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-brand-gray pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center border border-brand-gray">
                    <i className="fa-solid fa-hammer text-brand-oak" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold tracking-wider text-white">@toemmertonyheine</h3>
                    <p className="text-[10px] text-brand-charcoal tracking-widest uppercase">Tømrerarbejde & Værksted</p>
                  </div>
                </div>
                <a
                  href="https://www.instagram.com/toemmertonyheine/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs tracking-wider text-white hover:text-brand-oak uppercase font-medium"
                >
                  Følg
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
